import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import client from '../../api/client';

export const card = 'bg-white rounded-2xl border border-[#EFE3D8] p-5';
export const inputCls = 'w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]';
export const btnCls = 'text-xs px-4 py-2 rounded-full bg-[#7B2C3B] text-white disabled:opacity-40 inline-flex items-center gap-1.5 hover:bg-[#6A2433] transition-colors';

const FIELDS = [
  ['support_email', 'Support email'],
  ['privacy_email', 'Privacy email'],
  ['legal_email', 'Legal email'],
  ['support_phone', 'Support phone'],
  ['business_address', 'Business mailing address'],
];

export default function LegalConfigEditor({ config, toast, onSaved }) {
  const [f, setF] = useState(() => ({
    ...Object.fromEntries(FIELDS.map(([k]) => [k, config[k] || ''])),
    dba_registered: !!config.dba_registered,
    dba_approved_text: config.dba_approved_text || '',
    ai_disclosure_default: config.ai_disclosure_default || '',
    recording_disclosure_default: config.recording_disclosure_default || '',
  }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await client.put('/admin/legal/config', f);
      toast({ title: 'Legal config saved', description: 'Previous version archived.' });
      onSaved?.();
    } catch (e) {
      toast({ title: 'Could not save', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <section data-testid="legal-config-editor" className={card + ' space-y-3'}>
      <div>
        <h3 className="font-serif text-xl text-[#2A2A2A]">Legal identity & contacts</h3>
        <p className="text-xs text-[#6A6A6A]">Operator: <strong>{config.legal_company_name}</strong> · Public line: “{config.legal_relationship_text}”</p>
      </div>
      {FIELDS.map(([k, label]) => (
        <div key={k}>
          <label className="text-[11px] uppercase tracking-wide text-[#6A6A6A]">{label}</label>
          <input data-testid={`legal-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls + ' mt-1'} />
        </div>
      ))}
      <div className="rounded-xl bg-[#FBF7F2] border border-[#EFE3D8] p-3 space-y-2">
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input data-testid="legal-dba-toggle" type="checkbox" className="accent-[#7B2C3B] w-4 h-4" checked={f.dba_registered} onChange={(e) => setF({ ...f, dba_registered: e.target.checked })} />
          DBA is registered — switch public wording
        </label>
        <input data-testid="legal-dba-text" value={f.dba_approved_text} onChange={(e) => setF({ ...f, dba_approved_text: e.target.value })} placeholder="Half Genius Labs LLC d/b/a Your Wedding Friends." className={inputCls} />
        <p className="text-[11px] text-[#9A8A7E]">Only turn this on after the DBA is filed. The exact text you approve here replaces the footer line everywhere.</p>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wide text-[#6A6A6A]">Default AI greeting for new events</label>
        <textarea data-testid="legal-ai-default" rows={2} value={f.ai_disclosure_default} onChange={(e) => setF({ ...f, ai_disclosure_default: e.target.value })} className={inputCls + ' mt-1'} />
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wide text-[#6A6A6A]">Default call-recording notice</label>
        <input data-testid="legal-recording-default" value={f.recording_disclosure_default} onChange={(e) => setF({ ...f, recording_disclosure_default: e.target.value })} className={inputCls + ' mt-1'} />
      </div>
      <button data-testid="legal-config-save" onClick={save} disabled={saving} className={btnCls}>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save legal config
      </button>
    </section>
  );
}
